import { Component, DestroyRef, Input, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent } from 'primeng/tree';
import { SiteService } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { of, switchMap } from 'rxjs';
import { FinancialAccountProxyService } from '../../../proxies/financial-account.proxy.service';
import { FinancialAccount, PostingModule, SubledgerType } from '../../../models/financial-account/financial-account';
import { ResponseTranslation } from '../../../models/Translations';
import { SiteCompanyService } from '../../../services/site-company.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { allowsPostingFrom, postingModuleLabel } from '../../../utilities/posting-module.util';

/**
 * Reusable Financial Account (GL / chart-of-accounts) selector. Renders a PrimeNG TreeSelect of the
 * company's chart of accounts and, on selection, writes the account id into `referenceControl` (and,
 * if provided, the number/name into `numberControl`/`nameControl`). The localized account name is
 * shown beneath the field. In `readonly` mode it displays the value without offering a search; if a
 * name wasn't supplied it hydrates it once from the id.
 */
@Component({
  selector: 'app-financial-account-select',
  templateUrl: './financial-account-select.component.html',
  styleUrl: './financial-account-select.component.scss',
})
export class FinancialAccountSelectComponent implements OnInit {
  /** Holds the selected account id (reference). */
  @Input({ required: true }) referenceControl!: FormControl<string | null>;
  /** Optional — written with the account number on select (shown in the field box). */
  @Input() numberControl?: FormControl<string | null>;
  /** Optional — written with the localized account name on select (shown in the <small> tag). */
  @Input() nameControl?: FormControl<string | null>;

  @Input() label?: string;
  @Input() placeholder = 'Select account';
  @Input() required = false;
  @Input() readonly = false;

  /** Only posting (leaf) accounts are selectable. */
  @Input() postingOnly = true;
  /**
   * List only what can actually be picked (the default).
   *
   * <p>An account that cannot be selected — blocked, reserved for another module, the wrong subledger — is
   * dropped from the tree rather than rendered greyed out. Header accounts survive, but only where a
   * selectable account still sits somewhere beneath them: they are how you reach those accounts, so
   * removing them would take the leaves with them, but a branch that leads nowhere is just a dead end to
   * open and close again.</p>
   *
   * <p>Set false to render the whole chart of accounts with the unusable rows greyed instead — useful when
   * the point is to show a user that an account exists and why they may not post to it.</p>
   */
  @Input() onlySelectable = true;
  /** Optional: only accounts of this subledger type are selectable. */
  @Input() subledgerType?: SubledgerType;
  /**
   * Optional: refuse every account that has a subledger type at all (Provider / Client / Bank).
   *
   * <p>For screens whose lines cannot carry a subledger reference — the manual journal entry (#5077) is the
   * case in point. `JournalEntryPostingValidator` requires a matching `Subledger` on any controlled-account
   * line, so such an account would be accepted by the picker and then refused at post time, after the whole
   * entry had been typed. Greying it here turns a rejection into a rule the user can see.</p>
   */
  @Input() excludeSubledgerAccounts = false;
  /** Optional: only accounts that authorize this posting module are selectable (e.g. CashManagement). */
  @Input() set postingModule(value: PostingModule | undefined) {
    this.postingModuleSig.set(value);
  }
  get postingModule(): PostingModule | undefined {
    return this.postingModuleSig();
  }
  private readonly postingModuleSig = signal<PostingModule | undefined>(undefined);
  /** Optional: only bank accounts not yet linked to a bank are selectable. */
  @Input() unlinkedBankOnly = false;
  /** Optional allow-list of account NUMBERS — when set, the tree is pruned to just these accounts plus the
   * ancestor chain needed to reach them (parents render for navigation but stay non-selectable). Used to
   * limit AP lines to an item's configured GL accounts. */
  @Input() allowedAccountNumbers?: string[] | null;
  /** Optional override; otherwise resolved from the selected site. */
  @Input() financialCompanyReference?: string;

  private readonly destroyRef = inject(DestroyRef);
  private readonly proxy = inject(FinancialAccountProxyService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly locale = inject(LocaleHelperService);
  private readonly translate = inject(TranslateService);

  /** Human name of the module this field posts from — drives the hint and the greyed-row legend. */
  readonly moduleLabel = computed(() =>
    this.postingModuleSig() === undefined ? '' : postingModuleLabel(this.postingModuleSig()!, (k, f) => this.t(k, f)),
  );

  /**
   * Why rows are greyed. Shown as a tooltip on the label and as a legend in the panel — without it a leaf
   * account that simply refuses to be clicked reads as a broken field rather than a rule.
   */
  readonly filterHint = computed(() => {
    const module = this.moduleLabel();

    // Hiding and greying need different words. "Cannot be selected" explains a row the user can see; it
    // says nothing useful about a chart of accounts that is simply shorter than they expected.
    if (this.onlySelectable) {
      const parts = [this.t('COA_Select_Hint_OnlySelectable', 'Only accounts this field can post to are listed.')];
      if (module)
        parts.push(
          this.t(
            'COA_Select_Hint_OnlySelectable_Module',
            'It posts from {module}, so accounts reserved for another module are not shown.',
          ).replace('{module}', module),
        );
      if (this.excludeSubledgerAccounts)
        parts.push(
          this.t(
            'COA_Select_Hint_OnlySelectable_NoSubledger',
            'Control accounts (provider, client and bank) are not shown either — those post from their own module, which records who the balance belongs to.',
          ),
        );
      return parts.join(' ');
    }

    const parts = [this.t('COA_Select_Hint_Base', 'Header accounts and blocked accounts cannot be selected.')];
    if (this.excludeSubledgerAccounts)
      parts.push(
        this.t(
          'COA_Select_Hint_NoSubledger',
          'Control accounts (provider, client and bank) are greyed out — those post from their own module, which records who the balance belongs to.',
        ),
      );
    if (module)
      parts.push(
        this.t(
          'COA_Select_Hint_Module',
          'Accounts reserved for another module are also greyed out — this field posts from {module}.',
        ).replace('{module}', module),
      );
    return parts.join(' ');
  });

  treeNodes = signal<TreeNode[]>([]);
  loading = signal(false);
  selectedValue: TreeNode | null = null;
  private readonly nodeById = new Map<string, TreeNode>();

  ngOnInit(): void {
    if (this.readonly) {
      this.initReadonly();
    } else {
      this.loadTree();
    }

    // The bound control can be written by something other than this picker — an AP goods line takes its
    // account from the item master (#4306) after this component has already initialised, and the manual
    // "reload items" action moves it again. Both used to leave the form holding an account while the field
    // still showed its placeholder.
    this.referenceControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((reference) => this.syncFromExternalChange(reference));
  }

  /**
   * Re-renders the field when the account was set from outside.
   *
   * <p>A value that already matches what is displayed is ignored — a selection made HERE has been rendered
   * by {@link onNodeSelect} before the control ever emits, and rebuilding the node would be work that
   * changes nothing.</p>
   */
  private syncFromExternalChange(reference: string | null): void {
    const displayed = (this.selectedValue?.data as FinancialAccount | undefined)?.id ?? null;
    if (reference === displayed) return;

    if (!reference) {
      this.selectedValue = null;
      return;
    }

    const node = this.nodeById.get(reference);
    if (node) {
      this.selectedValue = node;
      this.syncControlsFrom(node.data as FinancialAccount, true);
      return;
    }

    // Not in the tree — read-only mode never built one, and a filtered tree may legitimately exclude it.
    // Fetch it so the field can show what the form actually holds.
    this.proxy.getById(reference).subscribe({
      next: (response) => {
        const acc = response.data;
        if (!acc) return;
        this.selectedValue = { key: acc.id, label: this.nodeLabel(acc), data: acc };
        this.syncControlsFrom(acc, true);
      },
      error: () => undefined,
    });
  }

  // ---- display helpers ----
  private localized(value: ResponseTranslation | null | undefined): string {
    return this.locale.getLocalizedValue(value);
  }

  private nodeLabel(acc: FinancialAccount): string {
    const base = `${acc.accountNumber}  ${this.localized(acc.accountName)}`.trim();
    // Name the owner on the one case the user cannot see for themselves: a leaf account, not blocked,
    // that still refuses to be picked because another module controls it.
    const module = this.postingModuleSig();
    if (module !== undefined && acc.isPostingAccount && !acc.isBlocked && !allowsPostingFrom(acc, module)) {
      const owners = (acc.authorizedPostingModules ?? []).map((m) => postingModuleLabel(m, (k, f) => this.t(k, f))).join(', ');
      return `${base}  ·  ${this.t('COA_Select_OwnedBy', 'only')} ${owners}`;
    }
    return base;
  }

  private t(key: string, fallback: string): string {
    const value = this.translate.instant(key);
    return value === key ? fallback : value;
  }

  nameValue(): string {
    return this.nameControl?.value ?? '';
  }

  /** The selected account number — shown as the small secondary text beneath the name, or as the primary
   * value when no name is available. Falls back to the selected node's data when no numberControl is bound. */
  numberValue(): string {
    return this.numberControl?.value ?? (this.selectedValue?.data as { accountNumber?: string } | undefined)?.accountNumber ?? '';
  }

  // ---- read-only ----
  private initReadonly(): void {
    this.selectedValue = this.buildReadonlyNode();
    const id = this.referenceControl?.value;
    if (id && !this.nameControl?.value) {
      this.proxy.getById(id).subscribe({
        next: (response) => {
          const acc = response.data;
          if (!acc) return;
          if (this.numberControl && !this.numberControl.value) this.numberControl.setValue(acc.accountNumber ?? null, { emitEvent: false });
          this.nameControl?.setValue(this.localized(acc.accountName), { emitEvent: false });
          this.selectedValue = this.buildReadonlyNode();
        },
        error: () => undefined,
      });
    }
  }

  private buildReadonlyNode(): TreeNode | null {
    const num = this.numberControl?.value;
    const name = this.nameValue();
    if (!num && !name) return null;
    return { label: [num, name].filter(Boolean).join('  '), data: { accountNumber: num } };
  }

  // ---- tree ----
  private loadTree(): void {
    this.loading.set(true);
    const companyId$ = this.financialCompanyReference
      ? of(this.financialCompanyReference)
      : this.siteCompany.getFinancialCompanyId(SiteService.getSelectedSiteId());

    companyId$.pipe(switchMap((companyId) => (companyId ? this.proxy.getByCompany(companyId) : of(null)))).subscribe({
      next: (response) => {
        this.treeNodes.set(this.buildTree(response?.data ?? []));
        this.preselect();
        this.loading.set(false);
      },
      error: () => {
        this.treeNodes.set([]);
        this.loading.set(false);
      },
    });
  }

  private buildTree(accounts: FinancialAccount[]): TreeNode[] {
    this.nodeById.clear();
    const childrenByParent = new Map<string, FinancialAccount[]>();
    const roots: FinancialAccount[] = [];
    for (const acc of accounts) {
      const parent = acc.parentAccountId;
      if (parent) {
        const list = childrenByParent.get(parent) ?? [];
        list.push(acc);
        childrenByParent.set(parent, list);
      } else {
        roots.push(acc);
      }
    }

    // When an allow-list is set, prune the tree to the allowed accounts plus the ancestor chain that
    // leads to them, so the picker shows only the item's GL accounts (and the parents needed to reach
    // them) rather than the whole chart of accounts.
    const allowList = this.allowedAccountNumbers?.length ? new Set(this.allowedAccountNumbers) : null;

    const byNumber = (a: FinancialAccount, b: FinancialAccount) => (a.accountNumber ?? '').localeCompare(b.accountNumber ?? '');
    const toNode = (acc: FinancialAccount): TreeNode | null => {
      const children = (childrenByParent.get(acc.id ?? '') ?? [])
        .sort(byNumber)
        .map(toNode)
        .filter((n): n is TreeNode => n !== null);
      const selectable = this.isSelectable(acc);
      // Drop a node that is neither allowed itself nor an ancestor of a kept (allowed) descendant.
      if (allowList && !allowList.has(acc.accountNumber) && children.length === 0) return null;
      // Same shape for the selectable filter: a node survives if it can be picked, or if something under
      // it can. Both tests run on the way back up, so `children` is already pruned — a header whose every
      // child was dropped has none left and goes with them.
      if (this.onlySelectable && !selectable && children.length === 0) return null;
      const node: TreeNode = {
        key: acc.id,
        label: this.nodeLabel(acc),
        data: acc,
        selectable,
        leaf: children.length === 0,
        children: children.length ? children : undefined,
      };
      if (acc.id) this.nodeById.set(acc.id, node);
      return node;
    };
    return roots
      .sort(byNumber)
      .map(toNode)
      .filter((n): n is TreeNode => n !== null);
  }

  private isSelectable(acc: FinancialAccount): boolean {
    if (this.postingOnly && !acc.isPostingAccount) return false;
    if (acc.isBlocked) return false;
    if (this.subledgerType !== undefined && acc.subledgerType !== this.subledgerType) return false;
    if (this.excludeSubledgerAccounts && acc.subledgerType != null) return false;
    // Mirrors JournalEntryPostingValidator: an EMPTY authorizedPostingModules means the account is
    // freely postable, and only a non-empty list restricts it to its owning module. Testing membership
    // alone treated the empty default as a denial, which hid every ordinary revenue and expense account —
    // the opposite of what the flag means, and stricter than the server would have been.
    if (this.postingModule !== undefined) {
      const controlledBy = acc.authorizedPostingModules ?? [];
      if (controlledBy.length > 0 && !controlledBy.includes(this.postingModule)) return false;
    }
    if (this.unlinkedBankOnly && acc.bankAccountReference) return false;
    // When an allow-list is provided, only those account numbers may be selected (ancestors kept for
    // navigation by buildTree remain non-selectable here).
    if (this.allowedAccountNumbers?.length && !this.allowedAccountNumbers.includes(acc.accountNumber)) return false;
    return true;
  }

  private preselect(): void {
    const id = this.referenceControl?.value;
    if (!id) {
      this.selectedValue = null;
      return;
    }
    const node = this.nodeById.get(id);
    if (node) {
      this.selectedValue = node;
      this.syncControlsFrom(node.data as FinancialAccount, false);
      return;
    }
    // Selected account isn't in the (filtered) tree — hydrate by id so it still displays.
    this.proxy.getById(id).subscribe({
      next: (response) => {
        const acc = response.data;
        if (!acc) return;
        this.selectedValue = { key: acc.id, label: this.nodeLabel(acc), data: acc };
        this.syncControlsFrom(acc, false);
      },
      error: () => undefined,
    });
  }

  private syncControlsFrom(acc: FinancialAccount, force: boolean): void {
    if (this.numberControl && (force || !this.numberControl.value))
      this.numberControl.setValue(acc.accountNumber ?? null, { emitEvent: false });
    if (this.nameControl && (force || !this.nameControl.value))
      this.nameControl.setValue(this.localized(acc.accountName), { emitEvent: false });
  }

  // ---- events ----
  onNodeSelect(event: TreeNodeSelectEvent): void {
    const acc = event.node?.data as FinancialAccount | undefined;
    if (!acc?.id) return;
    this.referenceControl.setValue(acc.id);
    this.referenceControl.markAsTouched();
    this.syncControlsFrom(acc, true);
  }

  onClear(): void {
    this.selectedValue = null;
    this.referenceControl.setValue(null);
    this.referenceControl.markAsTouched();
    this.numberControl?.setValue(null);
    this.nameControl?.setValue(null);
  }
}
